import React, { useState, useCallback, memo } from 'react';
import { Flex, Drawer, Button, Input, Pagination, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useSession } from 'next-auth/react';
import { ProductCard } from '@ozoneuniverse/ui-components/build-pkg/src/components/asset-store/products/ProductCard';
import { SIDE_MENU_EVENTS } from '@src/components/menu/sideMenu';
import { useGetBoughtAssetsByUserQuery } from '@src/components/generated/graphql';
import { debounce } from 'lodash';
import ModelViewerModal from './ModelViewer';
import { SearchOutlined } from '@ant-design/icons';
import styles from './web2-assets-store.module.css';

const FORMATS_TYPES = {
  MODEL: 2,
  IMAGE: 8,
  AUDIO: 6,
  VIDEO: 7,
};

const PAGE_LIMIT = 18;

const Cards = ({ assetsBought, onOpenPreview, onLoadAsset, loading }) => {
  return (
    <Flex gap={20} wrap='wrap'>
      {loading ? (
        <Spin
          indicator={
            <LoadingOutlined style={{ fontSize: 30, color: '#000' }} spin />
          }
        />
      ) : (
        <>
          {assetsBought?.length ? (
            assetsBought?.map((el, i) => {
              return (
                <ProductCard
                  product={el}
                  disableLink={true}
                  disableButtons={true}
                  disablePreview={el.assetURL.includes('AssetPack.gltf')}
                  type='web2'
                  imgOnClick={
                    el.assetURL.includes('AssetPack.gltf')
                      ? undefined
                      : () => onOpenPreview(el.assetURL)
                  }
                  customButton={{
                    text: 'Add to the scene',
                    onClick: () =>
                      onLoadAsset(
                        el.assetURL,
                        el.name,
                        el.fileType,
                        el.previewImage
                      ),
                  }}
                  key={i}
                />
              );
            })
          ) : (
            <span>You don't have assets yet.</span>
          )}
        </>
      )}
    </Flex>
  );
};

const CardsMemo = memo(Cards);

export default function MyInventory({
  isOpen,
  setIsOpen,
  loadAsset,
  loadScene,
}) {
  const { data: session } = useSession();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState('all');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [modelUrl, setModelUrl] = useState('');
  const [searchInput, setSearchInput] = useState(undefined);
  const [active, setActive] = useState(0);

  const { data, loading } = useGetBoughtAssetsByUserQuery({
    variables: {
      where: {
        userId: session?.user.id ?? '',
        offset: PAGE_LIMIT * (currentPage - 1),
        limit: PAGE_LIMIT,
        filter: {
          type: filterType,
          searchInput: searchInput,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const assetsBought = data?.getBoughtAssetsByUser.assets?.filter(
    (el) =>
      !el.assetURL.startsWith(
        'https://assets2022.s3.us-east-1.amazonaws.com/Dragon/RPG/3dModels'
      )
  );

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  function onLoadAsset(url, name, type, sceneThumbnail) {
    if (url.includes('AssetPack.gltf')) {
      const folderURL = url.split('AssetPack.gltf')[0];
      loadScene(name, folderURL, SIDE_MENU_EVENTS.SCENES, sceneThumbnail);
    } else {
      loadAsset(name, url, FORMATS_TYPES[type]);
    }
    setIsOpen(false);
  }

  const getResults = debounce(async (name) => {
    setSearchInput(name);
  }, 800);

  const onSearch = async (input) => {
    if (input.target.value.length <= 1) {
      getResults.cancel();
      return setSearchInput(undefined);
    }

    return getResults(input.target.value);
  };

  return (
    <>
      <Drawer
        open={isOpen}
        onClose={() => setIsOpen(false)}
        closeIcon={false}
        footer={false}
        width={'90%'}
        zIndex={1001}
        className={styles.assetsDrawer}
        title={
          <div className={styles.topSection}>
            <h3 className={styles.title}>Your Inventory</h3>
            <Flex align='center' justify='space-between'>
              <Flex align='flex-start' gap={25}>
                <Button
                  data-cy='inventory-all'
                  onClick={() => {
                    setActive(0);
                    setFilterType('all');
                  }}
                  className={`${active === 0 && styles.active} ${
                    styles.button
                  }`}
                >
                  <span className={styles.buttonText}>All</span>
                </Button>
                <Button
                  data-cy='inventory-prefabs'
                  onClick={() => {
                    setActive(1);
                    setFilterType('prefab');
                  }}
                  className={`${active === 1 && styles.active} ${
                    styles.button
                  }`}
                >
                  <span className={styles.buttonText}>Prefabs</span>
                </Button>
                <Button
                  data-cy='inventory-single-assets'
                  onClick={() => {
                    setActive(2);
                    setFilterType('single_asset');
                  }}
                  className={`${active === 2 && styles.active} ${
                    styles.button
                  }`}
                >
                  <span className={styles.buttonText}>Single assets</span>
                </Button>
              </Flex>
              <Input
                style={{ width: 300 }}
                placeholder='Search assets'
                className={styles.search}
                onChange={onSearch}
                prefix={<SearchOutlined className={styles.iconSearch} />}
              />
            </Flex>
          </div>
        }
      >
        <CardsMemo
          assetsBought={assetsBought}
          onOpenPreview={onOpenPreview}
          onLoadAsset={onLoadAsset}
          loading={loading}
        />
        <Flex justify='center'>
          <Pagination
            defaultCurrent={1}
            pageSize={PAGE_LIMIT}
            total={data?.getBoughtAssetsByUser.totalCount}
            hideOnSinglePage
            showSizeChanger={false}
            onChange={(page) => {
              setCurrentPage(page);
            }}
            className={styles.pagination}
          />
        </Flex>
        <ModelViewerModal
          open={previewOpen}
          setOpen={setPreviewOpen}
          modelUrl={modelUrl}
        />
      </Drawer>
    </>
  );
}
